/**
 * ScheduleAckBanner — "new schedule published · please acknowledge".
 *
 * THE one version of this notice (operator, 2026-08-03). It used to be three: a
 * rich inline copy on My Day and on My Day's working copy (expandable shift
 * list, different wording — "1 change from your usual pattern"), a fourth on the
 * retired My Time, and this shell one. The two My Day copies are gone; this
 * compact banner rides the shell and shows on EVERY rail item of the persona it
 * belongs to.
 *
 * ROLE-SCOPED (operator, 2026-08-03: "this should be for his role and not appear
 * anywhere else on other personas as this is specific to his role when the
 * branch manager posts it"). The branch manager publishes a schedule; the staff
 * member acknowledges it. A manager or admin who also happens to hold
 * `myschedule.read` is not the audience, so the capability check alone was too
 * wide — the workforce ROLE decides.
 *
 * One `getMyTime` per session, shared by every screen, fetched only for the role
 * that can act on it.
 */
import { useEffect, useState } from "react";
import { Btn, Icon, T, alpha } from "@v0y4ge/jrni-ui";
import { useNav } from "./router";
import { can, getUser } from "./access";
import { toast } from "./toast";
import { getMyTime, acknowledgeSchedule, type MyTimeResponse } from "../screens/workforce/wf-api";
import { dayLabel } from "../screens/workforce/wf-shared";
import { useT } from "../lib/i18n";

type Update = NonNullable<MyTimeResponse["scheduleUpdate"]>;

let pending: Promise<Update | null> | null = null;
let acked = false;
const listeners = new Set<() => void>();

/**
 * Roles this notice belongs to: the staff member whose schedule was published.
 * A ROLE_CATALOG id (app/src/shell/access.ts), so it holds for any user in the
 * role rather than a persona id. Add a role here only if its holder is the one
 * being asked to acknowledge — not merely someone who can see a schedule.
 */
const ACK_ROLES = new Set(["Personal Banker"]);

export function ScheduleAckBanner() {
  const t = useT();
  const nav = useNav();
  const role = getUser().assignments?.workforce;
  const allowed = can("myschedule.read") && !!role && ACK_ROLES.has(role);
  const [update, setUpdate] = useState<Update | null>(null);
  const [busy, setBusy] = useState(false);
  const [, bump] = useState(0);

  useEffect(() => {
    if (!allowed) return;
    if (!pending) pending = getMyTime().then((r) => r.scheduleUpdate ?? null).catch(() => null);
    let live = true;
    void pending.then((u) => { if (live) setUpdate(u); });
    const l = () => bump((n) => n + 1);
    listeners.add(l);
    return () => { live = false; listeners.delete(l); };
  }, [allowed]);

  if (!allowed || !update || update.acknowledged || acked) return null;

  const ack = () => {
    setBusy(true);
    acknowledgeSchedule()
      .then(() => { acked = true; listeners.forEach((l) => l()); toast({ tone: "success", title: t("shell.scheduleAck.acknowledged", "Schedule acknowledged") }); })
      .catch((e: unknown) => toast({ tone: "danger", title: t("shell.scheduleAck.couldntAcknowledge", "Couldn't acknowledge"), subtitle: e instanceof Error ? e.message : String(e) }))
      .finally(() => setBusy(false));
  };

  return (
    <div role="status" data-testid="shell-schedule-ack" style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", padding: "9px 24px", background: T.p4, borderBottom: `1px solid ${alpha(T.p1, 22)}`, flexShrink: 0 }}>
      <Icon.calendar size={14} style={{ color: T.p1, flexShrink: 0 }} />
      <span style={{ fontSize: 13, fontWeight: 700, color: T.n1 }}>{t("shell.scheduleAck.published", "New schedule published")} · {update.weekLabel}</span>
      <span style={{ fontSize: 12.5, color: T.p1 }}>
        {update.shiftCount} {t("shell.scheduleAck.shifts", "shifts")} · {update.changedCount} {t("shell.scheduleAck.changed", "changed")} · {t("shell.scheduleAck.by", "acknowledge by")} {dayLabel(update.acknowledgeBy, "md")}
      </span>
      <span style={{ display: "flex", gap: 8, marginLeft: "auto", flexShrink: 0 }}>
        <Btn size="sm" variant="secondary" onClick={() => nav.go("workforce.scheduletime.sandbox", { mode: "week" })}>{t("shell.scheduleAck.review", "Review shifts")}</Btn>
        <Btn size="sm" disabled={busy} onClick={ack}><Icon.check size={12} /> {busy ? "…" : t("shell.scheduleAck.ackAll", "Acknowledge all")}</Btn>
      </span>
    </div>
  );
}
